/**
 * Vector + Hive product identities, and the shared family strip.
 *
 * These mirror `RadarMark` exactly (same 64x64 grammar from Brand Guidelines
 * 002 "One grid, three verbs"): the neutral cells are `currentColor` so each
 * mark inherits the surrounding text token and stays correct on the dark
 * default, the light theme, and `ForceTheme`-locked pages without a theme hook
 * or hydration flash. The verb element keeps the literal brand pink `#F90B8A`
 * -- it is the constant across every variant, and a mark must not re-color if
 * `--cta` is ever retuned.
 *
 * Geometry is taken verbatim from `public/product-suite -icons/vector-icon.svg`
 * and `hive-icon.svg`. Those static SVGs stay as press-kit/reference art; the
 * live UI renders these components so the marks tint with the theme (an
 * external SVG through `next/image` cannot inherit CSS color -- the same reason
 * `RadarMark` is inline, documented there).
 *
 * `RadarMark` stays in its own file (`./RadarMark`); it is imported here only
 * for the family strip so all three marks read as siblings.
 */

import Link from 'next/link'
import { RadarMark } from './RadarMark'

/** Brand pink. The verb element in every mark of the family. */
const VERB_PINK = '#F90B8A'

interface MarkProps {
  className?: string
  /**
   * Accessible name. Provide it only when the mark stands alone; when it sits
   * beside a visible product label it renders decorative so screen readers do
   * not announce the product twice.
   */
  label?: string
}

/** Vector — the verb is a triangle aimed at the top-right corner (direction
 *  with magnitude); the counter-shape is the same triangle, smaller, on the
 *  bottom-left (where you started). Vector shows the move. */
export function VectorMark({ className = '', label }: MarkProps) {
  return (
    <svg
      viewBox='0 0 64 64'
      fill='none'
      className={className}
      {...(label
        ? { role: 'img', 'aria-label': label }
        : { 'aria-hidden': true })}
    >
      <rect x='4' y='4' width='26' height='26' fill='currentColor' />
      <path d='M34 4 L60 4 L60 30 Z' fill={VERB_PINK} className='mark-verb' />
      <path d='M9 39 L25 39 L25 55 Z' fill='currentColor' />
      <rect x='34' y='34' width='26' height='26' fill='currentColor' />
    </svg>
  )
}

/** Hive — the verb is a cell divided into four (the colony working as one);
 *  the counter-shape is a single cell on the diagonal (the individual joining).
 *  Hive multiplies. */
export function HiveMark({ className = '', label }: MarkProps) {
  return (
    <svg
      viewBox='0 0 64 64'
      fill='none'
      className={className}
      {...(label
        ? { role: 'img', 'aria-label': label }
        : { 'aria-hidden': true })}
    >
      <rect x='4' y='4' width='26' height='26' fill='currentColor' />
      <rect
        x='34'
        y='4'
        width='11'
        height='11'
        fill={VERB_PINK}
        className='mark-verb'
      />
      <rect
        x='49'
        y='4'
        width='11'
        height='11'
        fill={VERB_PINK}
        className='mark-verb'
      />
      <rect
        x='34'
        y='19'
        width='11'
        height='11'
        fill={VERB_PINK}
        className='mark-verb'
      />
      <rect
        x='49'
        y='19'
        width='11'
        height='11'
        fill={VERB_PINK}
        className='mark-verb'
      />
      <rect x='11.5' y='41.5' width='11' height='11' fill='currentColor' />
      <rect x='34' y='34' width='26' height='26' fill='currentColor' />
    </svg>
  )
}

/**
 * Lockup sizing, matched to `RadarMark`'s `LOCKUP_SIZES` so Vector/Hive/Radar
 * lockups sit at identical scale when shown together.
 */
const LOCKUP_SIZES = {
  sm: {
    mark: 'h-7 w-7',
    gap: 'gap-2.5',
    word: 'text-[17px]',
    byline: 'text-[9px]',
  },
  md: {
    mark: 'h-10 w-10',
    gap: 'gap-3',
    word: 'text-[24px]',
    byline: 'text-[10px]',
  },
  lg: {
    mark: 'h-10 w-10 md:h-14 md:w-14',
    gap: 'gap-3 md:gap-4',
    word: 'text-[24px] md:text-[34px]',
    byline: 'text-[10px] md:text-[12px]',
  },
} as const

interface LockupProps {
  className?: string
  size?: keyof typeof LOCKUP_SIZES
  /** Renders the "by Pixelmojo" byline. On by default. */
  byline?: boolean
}

/**
 * Mark plus wordmark. The wordmark is real text (not SVG `<text>`) so it stays
 * selectable, accessible, and renders in Space Grotesk via `--font-display`.
 */
function ProductLockup({
  Mark,
  word,
  className = '',
  size = 'sm',
  byline = true,
}: LockupProps & {
  Mark: (props: MarkProps) => React.ReactElement
  word: string
}) {
  const s = LOCKUP_SIZES[size]
  return (
    <span className={`inline-flex items-center ${s.gap} ${className}`}>
      <Mark className={`${s.mark} shrink-0`} />
      <span className='flex flex-col justify-center'>
        <span
          className={`font-display ${s.word} font-semibold leading-none tracking-[-0.02em] text-foreground`}
        >
          {word}
        </span>
        {byline && (
          <span
            className={`mt-1 font-mono ${s.byline} font-medium uppercase leading-none tracking-[0.18em] text-muted-foreground`}
          >
            by Pixelmojo
          </span>
        )}
      </span>
    </span>
  )
}

export function VectorLockup(props: LockupProps) {
  return <ProductLockup Mark={VectorMark} word='Vector' {...props} />
}

export function HiveLockup(props: LockupProps) {
  return <ProductLockup Mark={HiveMark} word='Hive' {...props} />
}

/* ─────────────────────────────────────────────────────────────────────────
 * Family strip — "Radar sweeps. Vector points. Hive multiplies."
 * The "Three products, one bloodline" row from the brand guidelines, used at
 * the foot of every product page to cross-link the suite. Pass `current` to
 * mark the page you are on (it renders as a non-link "you are here" tile).
 * ───────────────────────────────────────────────────────────────────────── */

type ProductKey = 'radar' | 'vector' | 'hive'

export const PRODUCT_FAMILY: {
  key: ProductKey
  Mark: (props: MarkProps) => React.ReactElement
  name: string
  verb: string
  line: string
  role: string
  summary: string
  delivery: string
  href: string
}[] = [
  {
    key: 'radar',
    Mark: RadarMark,
    name: 'Radar',
    verb: 'sweeps',
    line: 'Sees what AI gets wrong.',
    role: 'Measures visibility',
    summary: 'AI visibility audits across 13 checks in one pass.',
    delivery: 'Self-serve, first check free',
    href: '/platform',
  },
  {
    key: 'vector',
    Mark: VectorMark,
    name: 'Vector',
    verb: 'points',
    line: 'Shows the move.',
    role: 'Automates follow-through',
    summary: 'B2B lead qualification across 12 scoring dimensions.',
    delivery: 'Custom build, 60 days',
    href: '/vector',
  },
  {
    key: 'hive',
    Mark: HiveMark,
    name: 'Hive',
    verb: 'multiplies',
    line: 'The colony works as one.',
    role: 'Builds AI experiences',
    summary: 'Coordinated AI co-workers for business operations.',
    delivery: 'Custom build, 12 weeks',
    href: '/hive',
  },
]

export function ProductFamilyStrip({
  current,
  className = '',
}: {
  current?: ProductKey
  className?: string
}) {
  return (
    <div className={className}>
      <p className='instrument-label text-center mb-3'>One grid, three verbs</p>
      <h2 className='text-2xl md:text-3xl font-bold text-foreground text-center mb-3'>
        Radar <span className='text-accent-pink'>sweeps</span>. Vector{' '}
        <span className='text-accent-pink'>points</span>. Hive{' '}
        <span className='text-accent-pink'>multiplies</span>.
      </h2>
      <p className='text-sm text-muted-foreground text-center max-w-xl mx-auto mb-8'>
        Same grammar, different verb. One system, built to grow.
      </p>
      <div className='grid gap-4 sm:grid-cols-3 max-w-4xl mx-auto'>
        {PRODUCT_FAMILY.map(({ key, Mark, name, verb, line, href }) => {
          const isCurrent = key === current
          const body = (
            <>
              <Mark className='h-9 w-9 shrink-0 text-foreground' />
              <span className='flex flex-col'>
                <span className='text-base font-semibold leading-none text-foreground'>
                  {name} <span className='text-accent-pink'>{verb}</span>
                </span>
                <span className='mt-1.5 text-xs text-muted-foreground'>
                  {line}
                </span>
              </span>
            </>
          )
          return isCurrent ? (
            <div
              key={key}
              aria-current='true'
              className='flex items-center gap-3 border border-line-strong bg-muted/50 p-5'
            >
              {body}
            </div>
          ) : (
            <Link
              key={key}
              href={href}
              className='group flex items-center gap-3 border border-line-strong bg-card p-5 transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            >
              {body}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
